import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import {
  Users, CreditCard, Store, UserPlus, TrendingUp, AlertTriangle,
  Clock, FileText, MessageSquare, ChevronRight, Calendar, Activity,
  DollarSign, CheckCircle, XCircle, Eye
} from "lucide-react";

const DashboardTab = ({
  stats,
  users,
  businesses,
  memberships,
  businessApplications,
  crmLeads,
  transactions,
  safeFormat,
  onNavigateToTab,
  userRole
}) => {
  // Calculate metrics
  const pendingApplications = businessApplications.filter(a => a.status === 'pending');
  const newLeads = crmLeads.filter(l => l.status === 'new');
  const recentLeads = crmLeads.slice(0, 5);
  
  // Get memberships expiring within 7 days
  const now = new Date();
  const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const expiringMemberships = memberships.filter(m => {
    if (m.status !== 'active') return false;
    const expiryDate = new Date(m.expiry_date);
    return expiryDate >= now && expiryDate <= sevenDaysFromNow;
  });

  // Recent transactions (last 5)
  const recentTransactions = transactions.slice(0, 5);

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      new: "bg-blue-500/20 text-blue-400",
      contacted: "bg-yellow-500/20 text-yellow-400",
      interested: "bg-purple-500/20 text-purple-400",
      converted: "bg-green-500/20 text-green-400",
      lost: "bg-red-500/20 text-red-400",
      active: "bg-green-500/20 text-green-400",
      expired: "bg-red-500/20 text-red-400",
      pending: "bg-yellow-500/20 text-yellow-400",
      approved: "bg-green-500/20 text-green-400",
      rejected: "bg-red-500/20 text-red-400"
    };
    return colors[status] || "bg-white/10 text-white/60";
  };

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 rounded-xl p-6 border border-[#D4AF37]/20">
        <h2 className="font-['Playfair_Display'] text-2xl text-white mb-2">
          Welcome to Advanta Plus Admin
        </h2>
        <p className="text-white/60">
          Here's what's happening with your membership platform today.
        </p>
      </div>

      {/* Territory Owner Stats */}
      {userRole === 'territory_owner' && stats && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white/50 text-sm">Members</p>
                  <p className="text-white text-2xl font-bold">{stats.total_users || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                  <Store className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white/50 text-sm">Businesses</p>
                  <p className="text-white text-2xl font-bold">{stats.total_businesses || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white/50 text-sm">Active Memberships</p>
                  <p className="text-white text-2xl font-bold">{stats.active_memberships || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white/50 text-sm">Revenue</p>
                  <p className="text-white text-2xl font-bold">€{(stats.total_revenue || 0).toFixed(0)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Territory Users & Businesses */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 overflow-hidden">
              <div className="p-4 border-b border-white/5 flex items-center justify-between">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#D4AF37]" />
                  Territory Members
                </h3>
                <Button variant="ghost" size="sm" onClick={() => onNavigateToTab('users')} className="text-[#D4AF37] hover:text-[#D4AF37]/80">
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <div className="divide-y divide-white/5">
                {users.length > 0 ? users.slice(0, 5).map((u) => (
                  <div key={u.id} className="p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{u.first_name} {u.last_name}</p>
                        <p className="text-white/50 text-sm">{u.email}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(u.membership_status)}`}>
                        {u.membership_status || 'none'}
                      </span>
                    </div>
                  </div>
                )) : (
                  <div className="p-8 text-center text-white/40">
                    <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>No members yet</p>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 overflow-hidden">
              <div className="p-4 border-b border-white/5 flex items-center justify-between">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Store className="w-5 h-5 text-[#D4AF37]" />
                  Territory Businesses
                </h3>
                <Button variant="ghost" size="sm" onClick={() => onNavigateToTab('businesses')} className="text-[#D4AF37] hover:text-[#D4AF37]/80">
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <div className="divide-y divide-white/5">
                {(businesses || []).length > 0 ? (businesses || []).slice(0, 5).map((b) => (
                  <div key={b.id} className="p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{b.name}</p>
                        <p className="text-white/50 text-sm">{b.category} • {b.location}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(b.status)}`}>
                        {b.status}
                      </span>
                    </div>
                  </div>
                )) : (
                  <div className="p-8 text-center text-white/40">
                    <Store className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>No businesses yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alert Cards (admin only) */}
      {userRole !== 'territory_owner' && (pendingApplications.length > 0 || expiringMemberships.length > 0 || newLeads.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Pending Applications Alert */}
          {pendingApplications.length > 0 && (
            <div 
              className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 cursor-pointer hover:bg-yellow-500/15 transition-colors"
              onClick={() => onNavigateToTab('applications')}
              data-testid="pending-apps-alert"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-yellow-400" />
                </div>
                <div className="flex-1">
                  <p className="text-yellow-400 font-medium">{pendingApplications.length} Pending Applications</p>
                  <p className="text-white/50 text-sm">Businesses waiting for approval</p>
                </div>
                <ChevronRight className="w-5 h-5 text-yellow-400/50" />
              </div>
            </div>
          )}

          {/* Expiring Memberships Alert */}
          {expiringMemberships.length > 0 && (
            <div 
              className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4 cursor-pointer hover:bg-orange-500/15 transition-colors"
              onClick={() => onNavigateToTab('memberships')}
              data-testid="expiring-memberships-alert"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-orange-400" />
                </div>
                <div className="flex-1">
                  <p className="text-orange-400 font-medium">{expiringMemberships.length} Expiring Soon</p>
                  <p className="text-white/50 text-sm">Memberships expiring in 7 days</p>
                </div>
                <ChevronRight className="w-5 h-5 text-orange-400/50" />
              </div>
            </div>
          )}

          {/* New Leads Alert */}
          {newLeads.length > 0 && (
            <div 
              className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 cursor-pointer hover:bg-blue-500/15 transition-colors"
              onClick={() => onNavigateToTab('crm')}
              data-testid="new-leads-alert"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex-1">
                  <p className="text-blue-400 font-medium">{newLeads.length} New Leads</p>
                  <p className="text-white/50 text-sm">Leads requiring attention</p>
                </div>
                <ChevronRight className="w-5 h-5 text-blue-400/50" />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main Grid (admin/team only) */}
      {userRole !== 'territory_owner' && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Leads */}
        <div className="bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 overflow-hidden">
          <div className="p-4 border-b border-white/5 flex items-center justify-between">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#D4AF37]" />
              Recent Leads
            </h3>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onNavigateToTab('crm')}
              className="text-[#D4AF37] hover:text-[#D4AF37]/80"
            >
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="divide-y divide-white/5">
            {recentLeads.length > 0 ? (
              recentLeads.map((lead) => (
                <div key={lead.id} className="p-4 hover:bg-white/5 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">{lead.name}</p>
                      <p className="text-white/50 text-sm">{lead.email || lead.company || 'No contact info'}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(lead.status)}`}>
                        {lead.status}
                      </span>
                      {lead.value > 0 && (
                        <span className="text-[#D4AF37] text-sm font-medium">€{lead.value}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-white/40">
                <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>No leads yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Pending Applications */}
        <div className="bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 overflow-hidden">
          <div className="p-4 border-b border-white/5 flex items-center justify-between">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#D4AF37]" />
              Business Applications
            </h3>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onNavigateToTab('applications')}
              className="text-[#D4AF37] hover:text-[#D4AF37]/80"
            >
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="divide-y divide-white/5">
            {pendingApplications.length > 0 ? (
              pendingApplications.slice(0, 5).map((app) => (
                <div key={app.id} className="p-4 hover:bg-white/5 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">{app.business_name}</p>
                      <p className="text-white/50 text-sm">{app.name} • {app.category}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(app.status)}`}>
                      {app.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-white/40">
                <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>No pending applications</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Sales */}
        <div className="bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 overflow-hidden">
          <div className="p-4 border-b border-white/5 flex items-center justify-between">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#D4AF37]" />
              Recent Sales
            </h3>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onNavigateToTab('transactions')}
              className="text-[#D4AF37] hover:text-[#D4AF37]/80"
            >
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="divide-y divide-white/5">
            {recentTransactions.length > 0 ? (
              recentTransactions.map((tx) => (
                <div key={tx.id} className="p-4 hover:bg-white/5 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium capitalize">{tx.plan_id?.replace('_', ' ')}</p>
                      <p className="text-white/50 text-sm">{safeFormat(tx.created_at, 'MMM d, yyyy HH:mm')}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[#D4AF37] font-bold">€{tx.amount?.toFixed(2)}</p>
                      {tx.affiliate_code && (
                        <p className="text-white/40 text-xs">via {tx.affiliate_code}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-white/40">
                <DollarSign className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>No sales yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Expiring Memberships */}
        <div className="bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 overflow-hidden">
          <div className="p-4 border-b border-white/5 flex items-center justify-between">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#D4AF37]" />
              Expiring Soon
            </h3>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onNavigateToTab('memberships')}
              className="text-[#D4AF37] hover:text-[#D4AF37]/80"
            >
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="divide-y divide-white/5">
            {expiringMemberships.length > 0 ? (
              expiringMemberships.slice(0, 5).map((m) => {
                const user = users.find(u => u.id === m.user_id);
                const daysLeft = Math.ceil((new Date(m.expiry_date) - now) / (1000 * 60 * 60 * 24));
                return (
                  <div key={m.id} className="p-4 hover:bg-white/5 transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{user?.first_name} {user?.last_name}</p>
                        <p className="text-white/50 text-sm">{m.plan_name}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-medium ${daysLeft <= 2 ? 'text-red-400' : 'text-orange-400'}`}>
                          {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
                        </p>
                        <p className="text-white/40 text-xs">{safeFormat(m.expiry_date, 'MMM d')}</p>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-8 text-center text-white/40">
                <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>No memberships expiring soon</p>
              </div>
            )}
          </div>
        </div>
      </div>}

      {/* Quick Actions */}
      <div className="bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 p-6">
        <h3 className="font-semibold text-white mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button 
            onClick={() => onNavigateToTab('crm')}
            className="bg-white/5 hover:bg-white/10 text-white border border-white/10 h-auto py-4 flex flex-col items-center gap-2"
          >
            <MessageSquare className="w-6 h-6 text-[#D4AF37]" />
            <span>Add Lead</span>
          </Button>
          <Button 
            onClick={() => onNavigateToTab('businesses')}
            className="bg-white/5 hover:bg-white/10 text-white border border-white/10 h-auto py-4 flex flex-col items-center gap-2"
          >
            <Store className="w-6 h-6 text-[#D4AF37]" />
            <span>Add Business</span>
          </Button>
          <Button 
            onClick={() => onNavigateToTab('affiliates')}
            className="bg-white/5 hover:bg-white/10 text-white border border-white/10 h-auto py-4 flex flex-col items-center gap-2"
          >
            <UserPlus className="w-6 h-6 text-[#D4AF37]" />
            <span>Add Affiliate</span>
          </Button>
          <Button 
            onClick={() => onNavigateToTab('marketing')}
            className="bg-white/5 hover:bg-white/10 text-white border border-white/10 h-auto py-4 flex flex-col items-center gap-2"
          >
            <Activity className="w-6 h-6 text-[#D4AF37]" />
            <span>Create Campaign</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DashboardTab;
